import { useEffect } from 'react';

/* ------------------------------------------------------------------ */
/* Design tokens — "Trust & Authority" (orange accent on slate/white)  */
/* Shared across the landing page and the standalone DPDP tool pages.  */
/* ------------------------------------------------------------------ */
export const ORANGE = '#FF6600';
export const ORANGE_DK = '#C2410C'; // orange-700 — passes 4.5:1 on white for text
export const ORANGE_SOFT = 'rgba(255,102,0,0.08)';
export const ORANGE_RING = 'rgba(255,102,0,0.45)';
export const GREEN = '#047857';
export const RED = '#DC2626';
export const BG = '#FFFFFF';
export const BG_SOFT = '#F8FAFC'; // slate-50
export const SURFACE = '#FFFFFF';
export const BORDER = '#E2E8F0'; // slate-200
export const TXT = '#0F172A'; // slate-900
export const TXT_DIM = '#475569'; // slate-600 (AA on white)

export const FONT_STACK = '"Inter","Roboto",sans-serif';

export const gradientText = {
  background: `linear-gradient(120deg, ${ORANGE} 0%, #FF8A1E 55%, ${ORANGE_DK} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

export const focusRing = {
  '&:focus-visible': { outline: `3px solid ${ORANGE_RING}`, outlineOffset: 2 },
};

export const card = {
  background: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: 3,
  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04)',
  transition: 'transform .3s cubic-bezier(.16,.84,.44,1), border-color .3s, box-shadow .3s',
};

export const primaryBtn = {
  px: 4,
  py: 1.4,
  fontWeight: 600,
  borderRadius: 2,
  color: '#fff',
  textTransform: 'none',
  fontSize: '0.95rem',
  background: `linear-gradient(135deg, ${ORANGE} 0%, #FF8A1E 100%)`,
  boxShadow: '0 6px 18px rgba(255,102,0,0.28)',
  ...focusRing,
  '&:hover': {
    background: `linear-gradient(135deg, #F25E00 0%, #FF8A1E 100%)`,
    boxShadow: '0 10px 26px rgba(255,102,0,0.36)',
  },
};

export const ghostBtn = {
  px: 4,
  py: 1.4,
  fontWeight: 600,
  borderRadius: 2,
  color: TXT,
  textTransform: 'none',
  fontSize: '0.95rem',
  border: `1px solid ${BORDER}`,
  background: '#fff',
  ...focusRing,
  '&:hover': { borderColor: ORANGE, background: ORANGE_SOFT },
};

/* ------------------------------------------------------------------ */
/* Locale-aware currency formatting (INR uses Indian lakh/crore scale) */
/* ------------------------------------------------------------------ */
export const localeFor = (cur) => (cur === 'INR' ? 'en-IN' : 'en');
export const fmtFull = (n, cur) =>
  new Intl.NumberFormat(localeFor(cur), { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n || 0);
export const fmtCompact = (n, cur) =>
  new Intl.NumberFormat(localeFor(cur), { style: 'currency', currency: cur, notation: 'compact', maximumFractionDigits: 1 }).format(
    n || 0
  );

/* ------------------------------------------------------------------ */
/* SEO helpers — title + JSON-LD injection for standalone tool pages   */
/* ------------------------------------------------------------------ */
export function useDocumentTitle(title) {
  useEffect(() => {
    if (!title) return undefined;
    const prev = document.title;
    document.title = title;
    return () => {
      document.title = prev;
    };
  }, [title]);
}

export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [id, data]);
}
